<template>
  <div class="skin-management">
    <div class="page-header">
      <h1 class="page-title">Skin management</h1>

      <!-- Toggle switch — only shown for game admins -->
      <div v-if="hasAdminEntries" class="view-toggle">
        <button
          :class="['toggle-opt', { active: typeFilter === 'mine' }]"
          @click="typeFilter = 'mine'"
        >My skins</button>
        <button
          :class="['toggle-opt', { active: typeFilter === 'defaults' }]"
          @click="typeFilter = 'defaults'"
        >Game default skins</button>
      </div>
    </div>

    <!-- Search filters -->
    <div class="filter-row">
      <input
        class="filter-input"
        type="text"
        v-model="filterSkin"
        placeholder="Filter by skin name..."
      />
      <input
        class="filter-input"
        type="text"
        v-model="filterProfile"
        placeholder="Filter by profile name..."
      />
      <select class="filter-input filter-select" v-model="filterGame">
        <option value="">All games</option>
        <option v-for="g in availableGames" :key="g.id" :value="g.id">{{ g.name }}</option>
      </select>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="state-msg">Loading your skin assignments…</div>

    <!-- Empty -->
    <div v-else-if="!loading && filteredItems.length === 0" class="state-msg">
      No skin assignments found.
    </div>

    <!-- List -->
    <div v-else class="usage-list">
      <div
        v-for="entry in filteredItems"
        :key="entry.itemInstanceId + '_' + entry.skinId"
        :class="['usage-row', { 'usage-row--admin': entry.costumeDefault === 1 }]"
      >
        <img
          class="usage-thumb"
          :src="entry.thumbnailUrl || '/Images/placeholder_skin.png'"
          :alt="entry.skinName"
          loading="lazy"
        />
        <div class="usage-info">
          <div class="usage-label">{{ entry.skinName }} &rarr; {{ resolveProfileName(entry) }}</div>
          <div class="usage-game-name">{{ entry.gameName }}</div>
        </div>
        <span v-if="entry.costumeDefault === 1" class="admin-badge">game default skin</span>
        <button
          class="remove-btn"
          :disabled="removing.has(entryKey(entry))"
          @click="requestRemove(entry)"
        >{{ removing.has(entryKey(entry)) ? '…' : 'Remove' }}</button>
      </div>
    </div>

    <!-- Confirmation modal for admin entry removal -->
    <div v-if="removeConfirmEntry" class="modal-backdrop" @click.self="removeConfirmEntry = null">
      <div class="modal-box">
        <div class="modal-header">
          <span>Remove game default skin</span>
          <button class="modal-close" @click="removeConfirmEntry = null">✕</button>
        </div>
        <div class="modal-body">
          <p>
            You are about to remove <strong>{{ removeConfirmEntry.skinName }}</strong> as the
            default skin for <strong>{{ resolveProfileName(removeConfirmEntry) }}</strong>.
          </p>
          <p class="modal-warning">
            ⚠️ Players will not see any asset for this profile until a new default skin is set.
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="removeConfirmEntry = null">Cancel</button>
          <button class="btn-confirm" @click="confirmRemove">Remove</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import config from '@/config.js';

export default {
  name: 'ManageSkinUsage',

  data() {
    return {
      rawItems: [],
      profileNameMap: {},   // profileId (raw ItemName string) → display name
      loading: true,
      loadingProfiles: false,
      filterSkin: '',
      filterProfile: '',
      filterGame: '',
      typeFilter: 'mine',   // 'mine' | 'defaults'
      removeConfirmEntry: null,
      removing: new Set(),
      serverUrl: config.serverUrl,
    };
  },

  computed: {
    hasAdminEntries() {
      return this.rawItems.some(e => e.costumeDefault === 1);
    },

    availableGames() {
      const seen = new Map();
      for (const e of this.rawItems) {
        if (e.gameId && !seen.has(e.gameId)) seen.set(e.gameId, e.gameName);
      }
      return [...seen.entries()].map(([id, name]) => ({ id, name }));
    },

    filteredItems() {
      let items = this.rawItems;

      if (this.typeFilter === 'mine')     items = items.filter(e => e.costumeDefault === 0);
      if (this.typeFilter === 'defaults') items = items.filter(e => e.costumeDefault === 1);

      if (this.filterGame) {
        items = items.filter(e => e.gameId === this.filterGame);
      }

      const skinQ    = this.filterSkin.trim().toLowerCase();
      const profileQ = this.filterProfile.trim().toLowerCase();

      if (skinQ) {
        items = items.filter(e => e.skinName.toLowerCase().includes(skinQ));
      }
      if (profileQ) {
        items = items.filter(e =>
          this.resolveProfileName(e).toLowerCase().includes(profileQ)
        );
      }

      return items;
    },
  },

  async mounted() {
    await this.fetchSkinUsages();
  },

  methods: {
    entryKey(entry) {
      return `${entry.itemInstanceId}_${entry.skinId}`;
    },

    resolveProfileName(entry) {
      return this.profileNameMap[entry.profileId] || entry.profileId;
    },

    async fetchSkinUsages() {
      this.loading = true;
      try {
        const res = await fetch('/GetMySkinUsages', {
          credentials: 'include',
        });
        const data = await res.json();
        if (data.error !== 0) {
          this.rawItems = [];
          return;
        }
        this.rawItems = data.result || [];
        // Kick off profile name resolution in background (non-blocking)
        this.resolveProfileNames();
      } catch {
        this.rawItems = [];
      } finally {
        this.loading = false;
      }
    },

    async resolveProfileNames() {
      // Collect unique gameIds from all entries
      const gameIds = [...new Set(this.rawItems.map(e => e.gameId).filter(Boolean))];
      if (gameIds.length === 0) return;

      this.loadingProfiles = true;
      try {
        const nameMap = {};

        await Promise.all(
          gameIds.map(async gameId => {
            try {
              // 1. Get LTS data path for this game
              const ltsRes = await fetch(`${this.serverUrl}/GetGameLTSPath/${gameId}`, {
                credentials: 'include',
              });
              if (!ltsRes.ok) return;
              const ltsData = await ltsRes.json();
              if (ltsData.error !== 0 || !ltsData.result) return;

              // Base URL = LTS result with manifest.json stripped
              const baseUrl = ltsData.result.replace(/manifest\.json$/, '');

              // 2. Fetch manifest to get file list
              const manifestRes = await fetch(baseUrl + 'manifest.json');
              if (!manifestRes.ok) return;
              const manifest = await manifestRes.json();

              const profileFiles = (manifest.files || []).filter(f => f.endsWith('/profiles.json'));

              // 3. Fetch all profiles.json files in parallel
              const profileResults = await Promise.all(
                profileFiles.map(file =>
                  fetch(baseUrl + file)
                    .then(r => r.ok ? r.json() : [])
                    .catch(() => [])
                )
              );

              // 4. Build profileId → name map
              // profile.Id is the full WDS ID (e.g. "profile_1764063526617") which is
              // stored verbatim as Item.ItemName in the DB — use it as-is.
              for (const profiles of profileResults) {
                if (!Array.isArray(profiles)) continue;
                for (const profile of profiles) {
                  if (profile.Id && profile.Name) {
                    nameMap[profile.Id] = profile.Name;
                  }
                }
              }
            } catch {
              // Non-blocking — fall back to raw profileId for this game
            }
          })
        );

        // Trigger reactivity by replacing the object
        this.profileNameMap = nameMap;
      } finally {
        this.loadingProfiles = false;
      }
    },

    requestRemove(entry) {
      if (entry.costumeDefault === 1) {
        this.removeConfirmEntry = entry;
      } else {
        this.doRemove(entry);
      }
    },

    async confirmRemove() {
      const entry = this.removeConfirmEntry;
      this.removeConfirmEntry = null;
      await this.doRemove(entry);
    },

    async doRemove(entry) {
      const key = this.entryKey(entry);
      this.removing = new Set(this.removing).add(key);
      try {
        const res = await fetch('/RemoveSkinUsage', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            itemInstanceId: entry.itemInstanceId,
            skinId:         entry.skinId,
            costumeDefault: entry.costumeDefault,
          }),
        });
        const data = await res.json();
        if (data.error === 0) {
          this.rawItems = this.rawItems.filter(
            e => !(e.itemInstanceId === entry.itemInstanceId && e.skinId === entry.skinId)
          );
        }
      } catch {
        // Silently ignore — row stays in list
      } finally {
        const next = new Set(this.removing);
        next.delete(key);
        this.removing = next;
      }
    },
  },
};
</script>

<style scoped>
.skin-management {
  min-height: 100vh;
  background: #1a1a2e;
  color: #e0e0e0;
  font-family: Arial, sans-serif;
  padding: 24px 16px;
  max-width: 900px;
  margin: 0 auto;
}

/* Header */
.page-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.page-title {
  margin: 0;
  font-size: 2rem;
  font-weight: bold;
  color: #ffffff;
  text-align: center;
}

/* View toggle switch */
.view-toggle {
  display: flex;
  border: 1px solid #0f3460;
  border-radius: 6px;
  overflow: hidden;
}

.toggle-opt {
  background: #16213e;
  color: #aaa;
  border: none;
  padding: 7px 18px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: background 0.2s, color 0.2s;
}
.toggle-opt + .toggle-opt {
  border-left: 1px solid #0f3460;
}
.toggle-opt:hover  { background: #0f3460; color: #e0e0e0; }
.toggle-opt.active { background: #1a4a80; color: #ffffff; }

/* Search filters */
.filter-row {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.filter-input {
  flex: 1;
  min-width: 180px;
  padding: 8px 10px;
  background: #16213e;
  border: 1px solid #0f3460;
  border-radius: 4px;
  color: #e0e0e0;
  font-size: 0.9rem;
}
.filter-input::placeholder { color: #666; }
.filter-input:focus { outline: none; border-color: #1a4a80; }

.filter-select {
  flex: 0 0 auto;
  min-width: 160px;
  cursor: pointer;
}

/* State messages */
.state-msg {
  color: #aaa;
  padding: 16px 0;
  text-align: center;
}

/* Usage list */
.usage-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.usage-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 14px;
  background: #16213e;
  border-radius: 6px;
}

.usage-row--admin {
  background: #1a4a80;
}

.usage-thumb {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
  background: #0a2a50;
}

.usage-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.usage-skin-name {
  font-weight: bold;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.usage-profile-name {
  font-size: 0.85rem;
  color: #bbb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.usage-label {
  font-size: 0.95rem;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.usage-game-name {
  font-size: 0.78rem;
  color: #888;
}

.admin-badge {
  font-size: 0.72rem;
  background: rgba(255, 255, 255, 0.15);
  color: #e0e0e0;
  border-radius: 3px;
  padding: 2px 7px;
  white-space: nowrap;
  flex-shrink: 0;
}

.remove-btn {
  background: #0f3460;
  color: #e0e0e0;
  border: none;
  border-radius: 4px;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 0.85rem;
  flex-shrink: 0;
  transition: background 0.2s;
}
.remove-btn:hover:not(:disabled) { background: #c0392b; }
.remove-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Confirmation modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  background: #16213e;
  border-radius: 8px;
  width: 420px;
  max-width: 95vw;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  background: #0f3460;
  font-weight: bold;
}

.modal-close {
  background: none;
  border: none;
  color: #aaa;
  font-size: 1.1rem;
  cursor: pointer;
}
.modal-close:hover { color: #fff; }

.modal-body {
  padding: 18px;
  font-size: 0.9rem;
  line-height: 1.5;
}

.modal-warning {
  color: #f5a623;
  margin-top: 10px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 18px;
  border-top: 1px solid #0f3460;
}

.btn-cancel {
  background: #0a2a50;
  color: #e0e0e0;
  border: none;
  border-radius: 4px;
  padding: 7px 18px;
  cursor: pointer;
  font-size: 0.9rem;
}
.btn-cancel:hover { background: #0f3460; }

.btn-confirm {
  background: #c0392b;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 7px 18px;
  cursor: pointer;
  font-size: 0.9rem;
}
.btn-confirm:hover { background: #e74c3c; }
</style>
